/**
 * Turning `navigation.sidebar`'s flat payload into the sidebar tree, and flattening it again
 * into the rows the virtualiser renders (spec M0-F4 §7.4).
 *
 * Two properties matter more than elegance here:
 *   - a folder cycle after a bad import must not hang the browser (E5), and
 *   - rows are keyed by id, never by name, because 14 board names are duplicated in this
 *     account (E6).
 */

export interface SidebarFolder {
  id: string;
  name: string;
  parentFolderId: string | null;
  color: string | null;
  position: string;
  /** `FolderFontWeight`, applied verbatim (F7 §8). Absent on every row today. */
  fontWeight?: string | null;
}

export interface SidebarLeaf {
  id: string;
  name: string;
  kind: string;
  state: string;
  type: 'board' | 'doc' | 'dashboard';
  folderId: string | null;
  position: string;
  isFavorite: boolean;
}

export interface SidebarPayload {
  workspace: { id: string; name: string; kind: string };
  folders: SidebarFolder[];
  boards: SidebarLeaf[];
  docs: SidebarLeaf[];
  dashboards: SidebarLeaf[];
}

export type TreeNode =
  | { kind: 'folder'; id: string; name: string; level: number; folder: SidebarFolder; children: TreeNode[] }
  | { kind: 'leaf'; id: string; name: string; level: number; leaf: SidebarLeaf };

export interface FlatRow {
  /** Unique across kinds: a folder and a board may share an id. */
  key: string;
  node: TreeNode;
  level: number;
}

function byPositionThenName(a: { position: string; name: string }, b: { position: string; name: string }) {
  if (a.position === b.position) return a.name.localeCompare(b.name);
  return a.position < b.position ? -1 : 1;
}

/**
 * Detects a `parent_folder_id` cycle and drops the back edge, rendering that subtree at the
 * root instead (E5). Returns the ids whose parent link was dropped so the caller can log once.
 */
export function breakFolderCycles(folders: SidebarFolder[]): {
  folders: SidebarFolder[];
  droppedEdges: string[];
} {
  const byId = new Map(folders.map((folder) => [folder.id, folder]));
  const droppedEdges: string[] = [];

  const resolved = folders.map((folder) => {
    const seen = new Set<string>([folder.id]);
    let cursor: string | null = folder.parentFolderId;
    while (cursor !== null) {
      if (seen.has(cursor)) {
        droppedEdges.push(folder.id);
        return { ...folder, parentFolderId: null };
      }
      seen.add(cursor);
      cursor = byId.get(cursor)?.parentFolderId ?? null;
    }
    // A parent that is not in the payload is the same problem with a different cause.
    if (folder.parentFolderId !== null && !byId.has(folder.parentFolderId)) {
      droppedEdges.push(folder.id);
      return { ...folder, parentFolderId: null };
    }
    return folder;
  });

  return { folders: resolved, droppedEdges };
}

/**
 * monday nests folders inside folders and mixes boards, docs and dashboards at every level
 * (`[ui:first-look/board-table.png]`). Folders come before leaves, both in position order.
 */
export function buildTree(payload: SidebarPayload): { nodes: TreeNode[]; droppedEdges: string[] } {
  const { folders, droppedEdges } = breakFolderCycles(payload.folders);
  const leaves = [...payload.boards, ...payload.docs, ...payload.dashboards];

  const childFolders = new Map<string | null, SidebarFolder[]>();
  for (const folder of folders) {
    const bucket = childFolders.get(folder.parentFolderId) ?? [];
    bucket.push(folder);
    childFolders.set(folder.parentFolderId, bucket);
  }

  const knownFolderIds = new Set(folders.map((folder) => folder.id));
  const childLeaves = new Map<string | null, SidebarLeaf[]>();
  for (const leaf of leaves) {
    // A board whose folder was deleted (or never arrived) belongs at the workspace root.
    const parent = leaf.folderId !== null && knownFolderIds.has(leaf.folderId) ? leaf.folderId : null;
    const bucket = childLeaves.get(parent) ?? [];
    bucket.push(leaf);
    childLeaves.set(parent, bucket);
  }

  function build(parentId: string | null, level: number): TreeNode[] {
    const folderNodes = (childFolders.get(parentId) ?? [])
      .sort(byPositionThenName)
      .map((folder): TreeNode => ({
        kind: 'folder',
        id: folder.id,
        name: folder.name,
        level,
        folder,
        children: build(folder.id, level + 1),
      }));
    const leafNodes = (childLeaves.get(parentId) ?? [])
      .sort(byPositionThenName)
      .map((leaf): TreeNode => ({ kind: 'leaf', id: leaf.id, name: leaf.name, level, leaf }));
    return [...folderNodes, ...leafNodes];
  }

  return { nodes: build(null, 0), droppedEdges };
}

export interface FlattenOptions {
  expandedFolderIds: ReadonlySet<string>;
  /** Name substring, case-insensitive. Matched ancestors stay visible (S6). */
  filter?: string;
}

/** Depth-first order, honouring the expanded set. This is what the virtualiser indexes. */
export function flattenTree(nodes: TreeNode[], options: FlattenOptions): FlatRow[] {
  const needle = options.filter?.trim().toLowerCase() ?? '';
  const filtering = needle.length > 0;

  const rows: FlatRow[] = [];

  function matches(node: TreeNode): boolean {
    if (node.name.toLowerCase().includes(needle)) return true;
    return node.kind === 'folder' && node.children.some(matches);
  }

  function walk(list: TreeNode[]) {
    for (const node of list) {
      if (filtering && !matches(node)) continue;
      rows.push({ key: `${node.kind}:${node.id}`, node, level: node.level });
      if (node.kind !== 'folder') continue;
      // While filtering, every ancestor of a match is open regardless of the stored state.
      const open = filtering || options.expandedFolderIds.has(node.id);
      if (open) walk(node.children);
    }
  }

  walk(nodes);
  return rows;
}

/** The folder ids on the path to `leafId`, so navigating to a board reveals it (S2). */
export function pathToLeaf(nodes: TreeNode[], leafId: string, leafType = 'board'): string[] {
  const path: string[] = [];

  function walk(list: TreeNode[], ancestors: string[]): boolean {
    for (const node of list) {
      if (node.kind === 'leaf') {
        if (node.id === leafId && node.leaf.type === leafType) {
          path.push(...ancestors);
          return true;
        }
        continue;
      }
      if (walk(node.children, [...ancestors, node.id])) return true;
    }
    return false;
  }

  walk(nodes, []);
  return path;
}

/**
 * B10 — the sidebar Content section's **Sort by alphabetical order**
 * (`[c:sidebar-content-menu]`). A pure re-sort of the built tree: folders keep their children,
 * every level is ordered by name with `localeCompare`, and the stored `position` is untouched,
 * because the toggle is a per-user view of the tree and not a write to anybody's order.
 */
export function sortTreeByName(nodes: TreeNode[]): TreeNode[] {
  const sorted = [...nodes].sort((a, b) => a.name.localeCompare(b.name));
  return sorted.map((node) =>
    node.kind === 'folder' ? { ...node, children: sortTreeByName(node.children) } : node,
  );
}
